// talk-create-frontend-vercel/components/AssistantDebugModal.tsx
import React, { useState, memo } from 'react';
import { AssistantTurnDebugInfo } from '#types/types';
import { X, ChevronRight, ChevronDown } from '@/components/ui/lucide-memo';
import { isInternalAssetUrl } from '#lib/azure-shared';

// --- Helper to identify image URLs ---
const isImageUrl = (url: unknown): boolean => {
  if (isInternalAssetUrl(url)) {
    return true;
  }
  if (typeof url !== 'string') return false;
  // Handle relative API paths
  if (url.startsWith('/api/tts-files/')) {
    return true;
  }
  // Handle common image file extensions, allowing for query parameters
  return /\.(jpeg|jpg|gif|png|webp|bmp)(\?.*)?$/i.test(url);
};


// --- JSON Viewer Components (Internal to this modal) ---

interface JsonNodeProps {
  nodeKey: string | number;
  value: any;
  defaultExpanded?: boolean;
}

const JsonNode: React.FC<JsonNodeProps> = ({ nodeKey, value, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isCollapsible = isObject || isArray;

  const handleToggle = () => {
    if (isCollapsible) {
      setIsExpanded(!isExpanded);
    }
  };

  const renderValue = () => {
    if (isObject) {
      const entries = Object.entries(value);
      return (
        <>
          <span className="text-neutral-500">{'{'}</span>
          {isExpanded ? (
            <div style={{ paddingLeft: '1.5rem' }}>
              {entries.map(([key, val]) => (
                <JsonNode key={key} nodeKey={key} value={val} />
              ))}
            </div>
          ) : (
            <span className="text-neutral-500 cursor-pointer hover:underline" onClick={handleToggle}> ... </span>
          )}
          <span className="text-neutral-500">{'}'}</span>
        </>
      );
    }

    if (isArray) {
      return (
        <>
          <span className="text-neutral-500">{'['}</span>
          {isExpanded ? (
            <div style={{ paddingLeft: '1.5rem' }}>
              {value.map((item, index) => (
                <JsonNode key={index} nodeKey={index} value={item} />
              ))}
            </div>
          ) : (
            <span className="text-neutral-500 cursor-pointer hover:underline" onClick={handleToggle}> ... </span>
          )}
          <span className="text-neutral-500">{']'}</span>
        </>
      );
    }

    // Primitive values
    const type = typeof value;
    let colorClass = '';
    let displayValue = String(value);

    if (type === 'string') {
      if (isImageUrl(value)) {
        return (
          <div className="my-2">
            <a href={value} target="_blank" rel="noopener noreferrer" title="Open image in new tab">
              <img
                src={value}
                alt={`Debug context image for key: ${nodeKey}`}
                className="max-w-full h-auto rounded-md border border-neutral-600"
                style={{ maxHeight: '300px' }}
                loading="lazy"
              />
            </a>
            <span className="text-green-400 block truncate text-xs mt-1">{`"${value}"`}</span>
          </div>
        );
      }
      colorClass = 'text-green-400';
      displayValue = `"${value}"`;
    } else if (type === 'number') {
      colorClass = 'text-blue-400';
    } else if (type === 'boolean') {
      colorClass = 'text-purple-400';
    } else if (value === null) {
      colorClass = 'text-orange-400';
      displayValue = 'null';
    }

    return <span className={colorClass}>{displayValue}</span>;
  };

  return (
    <div className="font-mono text-sm">
      <div className="flex items-start" onClick={handleToggle} style={{ cursor: isCollapsible ? 'pointer' : 'default' }}>
        {isCollapsible && (
          isExpanded ? <ChevronDown size={16} className="mr-1 flex-shrink-0 mt-0.5" /> : <ChevronRight size={16} className="mr-1 flex-shrink-0 mt-0.5" />
        )}
        <span className="text-rose-400 mr-2 flex-shrink-0" style={{ marginLeft: isCollapsible ? 0 : '20px' }}>{nodeKey}:</span>
        {!isCollapsible && renderValue()}
      </div>
      {isCollapsible && (
        <div style={{ marginLeft: '20px' }}>
          {renderValue()}
        </div>
      )}
    </div>
  );
};

// --- Main Modal Component ---

interface AssistantDebugModalProps {
  isOpen: boolean;
  onClose: () => void;
  debugInfo: AssistantTurnDebugInfo | null;
}

const AssistantDebugModal: React.FC<AssistantDebugModalProps> = ({ isOpen, onClose, debugInfo }) => {
  if (!isOpen || !debugInfo) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-neutral-800 text-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col p-6 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 className="text-xl font-semibold">Assistant Turn Debug Info</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-neutral-700">
            <X size={20} />
          </button>
        </div>
        <div className="flex-shrink-0">
            <p className="text-sm text-neutral-400 mb-1">Turn ID: <code className="bg-neutral-700 px-1 rounded">{debugInfo.turnId}</code></p>
            <p className="text-sm text-neutral-400 mb-4">Mode: <code className="bg-neutral-700 px-1 rounded">{debugInfo.requestBody.mode || 'director'}</code></p>
        </div>

        <div className="flex-grow overflow-y-auto bg-neutral-900 rounded p-4 custom-scrollbar min-h-0">
          <JsonNode nodeKey="requestBody" value={debugInfo.requestBody} defaultExpanded={true} />
        </div>
      </div>
    </div>
  );
};

export default memo(AssistantDebugModal);
